import { HearingPhaseBarDirective } from "../hearing-detail/hearing-phase-bar.directive";
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, Input, OnChanges, SimpleChanges, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { catchError, of } from "rxjs";

import {
  PhmsAuditActivityDisplayError,
  PhmsAuditActivityEntry,
  PhmsAuditActivityPerspective,
  formatAuditActivityCode,
  formatAuditActivityWhen,
  mapAuditActivityError,
} from "./phms-audit-activity.model";
import { PhmsAuditActivityService } from "./phms-audit-activity.service";

@Component({
  selector: "phms-audit-activity-log",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, HearingPhaseBarDirective],
  templateUrl: "./phms-audit-activity-log.component.html",
  styleUrl: "./phms-audit-activity-log.component.scss",
})
export class PhmsAuditActivityLogComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private readonly activityService = inject(PhmsAuditActivityService);

  @Input({ required: true }) perspectiveCode!: PhmsAuditActivityPerspective;
  @Input({ required: true }) subjectId!: number | null;
  @Input() title = "Audit Activity";
  @Input() emptyMessage = "No audit activity has been recorded for this record yet.";
  @Input() take = 25;
  @Input() embedded = false;
  private loadGeneration = 0;

  readonly isLoading = signal(false);
  readonly errorState = signal<PhmsAuditActivityDisplayError | null>(null);
  readonly entries = signal<readonly PhmsAuditActivityEntry[]>([]);

  readonly formatWhen = formatAuditActivityWhen;
  readonly formatCode = formatAuditActivityCode;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["perspectiveCode"] || changes["subjectId"] || changes["take"]) {
      this.load();
    }
  }

  load(): void {
    const generation = ++this.loadGeneration;
    if (!this.perspectiveCode || !this.subjectId || this.subjectId <= 0) {
      this.entries.set([]);
      this.errorState.set(null);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.errorState.set(null);
    this.entries.set([]);

    this.activityService
      .getActivity(this.perspectiveCode, this.subjectId, this.take)
      .pipe(
        catchError((error: unknown) => {
          if (generation === this.loadGeneration) this.errorState.set(mapAuditActivityError(error));
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((entries) => {
        if (generation !== this.loadGeneration) return;
        this.entries.set(entries);
        this.isLoading.set(false);
      });
  }

  entryMeta(entry: PhmsAuditActivityEntry): string {
    return [
      formatAuditActivityCode(entry.eventType),
      formatAuditActivityCode(entry.entityName),
      entry.entityId ? `#${entry.entityId}` : null,
    ]
      .filter((part): part is string => Boolean(part))
      .join(" | ");
  }

  trackByEntry(_index: number, entry: PhmsAuditActivityEntry): number {
    return entry.id;
  }
}
